// Unit + conversion engine for the Menu Costing Lab (menu-recipe-costing).
//
// Pure ESM — no Deno APIs — importable by both the edge function (index.ts)
// and the Node test runner (units.test.mjs), following the repo's
// core.mjs/test.mjs convention (see supabase/functions/setup-progress/).
//
// Everything reduces to three canonical bases:
//   mass   → grams  (g)
//   volume → milliliters (ml)
//   count  → each
// Mass↔volume bridging requires an explicit density; count↔mass/volume
// bridging requires a per-each size — explicit, or implied by the conversion's
// own case math (see effectiveEachSize). Nothing is guessed from outside data —
// missing bridges surface as unresolved conversions for the tester to fix.

import { readPack } from "../_shared/packText.mjs";

// ── Unit tables ───────────────────────────────────────────────────────────────

const MASS_UNITS = {
  g: 1, gram: 1, grams: 1, gr: 1,
  kg: 1000, kilo: 1000, kilogram: 1000, kilograms: 1000,
  mg: 0.001,
  lb: 453.59237, lbs: 453.59237, pound: 453.59237, pounds: 453.59237, "#": 453.59237,
  oz: 28.349523125, ounce: 28.349523125, ounces: 28.349523125,
};

const VOLUME_UNITS = {
  ml: 1, milliliter: 1, milliliters: 1, cc: 1,
  l: 1000, lt: 1000, liter: 1000, liters: 1000, litre: 1000, litres: 1000,
  gal: 3785.411784, gallon: 3785.411784, gallons: 3785.411784,
  qt: 946.352946, quart: 946.352946, quarts: 946.352946,
  pt: 473.176473, pint: 473.176473, pints: 473.176473,
  cup: 236.5882365, cups: 236.5882365, c: 236.5882365,
  floz: 29.5735295625, "fl oz": 29.5735295625, "fl-oz": 29.5735295625,
  "fluid ounce": 29.5735295625, "fluid ounces": 29.5735295625,
  tbsp: 14.78676478125, tablespoon: 14.78676478125, tablespoons: 14.78676478125, tbl: 14.78676478125,
  tsp: 4.92892159375, teaspoon: 4.92892159375, teaspoons: 4.92892159375,
};

const COUNT_UNITS = {
  each: 1, ea: 1, count: 1, ct: 1, cnt: 1, piece: 1, pieces: 1, pc: 1, pcs: 1,
  unit: 1, units: 1, item: 1, items: 1, portion: 1, portions: 1, serving: 1, servings: 1,
  dozen: 12, dz: 12, doz: 12,
};

// Kitchen fraction-of-a-whole units the models emit for garnish ("1 wedge of
// lemon", "half an onion"). These are ASSUMPTIONS about how a whole item is
// cut — conversions using them carry a visible warning. Slices are excluded
// on purpose: a slice's size depends on the product; recipes should state
// slices by weight instead.
const APPROX_COUNT_UNITS = {
  wedge: { factor: 1 / 6, note: "1/6 of one whole item" },
  wedges: { factor: 1 / 6, note: "1/6 of one whole item" },
  segment: { factor: 1 / 8, note: "1/8 of one whole item" },
  segments: { factor: 1 / 8, note: "1/8 of one whole item" },
  half: { factor: 0.5, note: "half of one whole item" },
  halves: { factor: 0.5, note: "half of one whole item" },
  quarter: { factor: 0.25, note: "a quarter of one whole item" },
  quarters: { factor: 0.25, note: "a quarter of one whole item" },
};

// Ounce is genuinely ambiguous ("8 oz of cream" is usually fluid). We default
// to MASS but flag it so callers can warn when the other side of a conversion
// is volumetric.
const AMBIGUOUS_OZ = new Set(["oz", "ounce", "ounces"]);

// Container-ish words seen in pack descriptions. They carry no size on their
// own — a "bag" is not convertible without an inner quantity.
const CONTAINER_WORDS = new Set([
  "case", "cs", "box", "bx", "bag", "bags", "sack", "sacks", "jar", "jars", "can", "cans",
  "bottle", "bottles", "btl", "carton", "cartons", "tub", "tubs", "pail", "pails",
  "tray", "trays", "pack", "packs", "pk", "flat", "loaf", "loaves", "roll", "rolls",
  "bunch", "bunches", "head", "heads", "sleeve", "sleeves", "container", "jug", "jugs",
  "tin", "tins",
]);

export const BASE_UNITS = { mass: "g", volume: "ml", count: "each" };

// A "unit" column of ml/g/cc/mg is data noise, not a real selling unit — no
// supplier prices food by the single millilitre or gram ($9.50 "per ml" of a
// 750 ml bottle would make the bottle $7,125). Such units must never anchor
// per-unit pricing.
function isTinyMeasureNorm(norm) {
  return !!norm && (norm.kind === "mass" || norm.kind === "volume") &&
    norm.factor != null && norm.factor <= 1.01;
}

// ── normalizeUnit ─────────────────────────────────────────────────────────────

/**
 * Normalize a raw unit string.
 * @returns {{ raw, key, kind: 'mass'|'volume'|'count'|'container'|'unknown',
 *             factor: number|null, base: 'g'|'ml'|'each'|null, ambiguousOz: boolean }}
 */
export function normalizeUnit(raw) {
  const out = { raw: raw ?? null, key: null, kind: "unknown", factor: null, base: null, ambiguousOz: false };
  if (raw == null) return out;
  let s = String(raw).trim().toLowerCase().replace(/\.+$/, "").replace(/\s+/g, " ");
  if (!s) return out;
  // strip trailing period-forms like "lb." handled above; unify fl-oz variants
  s = s.replace(/^fl\.? ?oz$/, "floz").replace(/^fluid ?(oz|ounce|ounces)$/, "floz");
  if (Object.prototype.hasOwnProperty.call(MASS_UNITS, s)) {
    return { ...out, key: s, kind: "mass", factor: MASS_UNITS[s], base: "g", ambiguousOz: AMBIGUOUS_OZ.has(s) };
  }
  if (Object.prototype.hasOwnProperty.call(VOLUME_UNITS, s)) {
    return { ...out, key: s, kind: "volume", factor: VOLUME_UNITS[s], base: "ml" };
  }
  if (Object.prototype.hasOwnProperty.call(COUNT_UNITS, s)) {
    return { ...out, key: s, kind: "count", factor: COUNT_UNITS[s], base: "each" };
  }
  if (Object.prototype.hasOwnProperty.call(APPROX_COUNT_UNITS, s)) {
    const approx = APPROX_COUNT_UNITS[s];
    return { ...out, key: s, kind: "count", factor: approx.factor, base: "each", approxNote: approx.note };
  }
  if (CONTAINER_WORDS.has(s)) {
    return { ...out, key: s, kind: "container" };
  }
  return out;
}

/** True when the string names a unit we can reduce to g/ml/each. */
export function isConvertibleUnit(raw) {
  const kind = normalizeUnit(raw).kind;
  return kind === "mass" || kind === "volume" || kind === "count";
}

// ── convertToBase ─────────────────────────────────────────────────────────────

/**
 * Convert a quantity to a target base, bridging kinds only when the needed
 * bridge value is provided.
 *
 * @param qty       number
 * @param unitRaw   string
 * @param targetBase 'g'|'ml'|'each'
 * @param bridges   { densityGPerMl?: number|null, eachSize?: {qty:number, unit:string}|null }
 * @returns {{ ok: boolean, qty: number|null, reason: string|null, warnings: string[] }}
 *   reason ∈ needs_density | needs_each_size | unknown_unit | unsupported | bad_qty
 */
export function convertToBase(qty, unitRaw, targetBase, bridges = {}) {
  const warnings = [];
  // null/empty quantities are UNRESOLVED, not zero — Number(null) === 0 would
  // silently cost a "to taste" line at $0 and mark the recipe complete.
  if (qty == null || qty === "") return { ok: false, qty: null, reason: "bad_qty", warnings };
  const n = Number(qty);
  if (!Number.isFinite(n) || n < 0) return { ok: false, qty: null, reason: "bad_qty", warnings };
  const u = normalizeUnit(unitRaw);
  if (u.kind === "unknown" || u.kind === "container") {
    return { ok: false, qty: null, reason: "unknown_unit", warnings };
  }
  if (u.approxNote) warnings.push(`Interpreted '${u.key}' as ${u.approxNote}.`);

  const sameKind = (u.base === targetBase);
  if (sameKind) {
    return { ok: true, qty: n * u.factor, reason: null, warnings };
  }

  const density = Number(bridges.densityGPerMl);
  const hasDensity = Number.isFinite(density) && density > 0;

  // mass → ml or volume → g requires density.
  if (u.kind === "mass" && targetBase === "ml") {
    if (!hasDensity) return { ok: false, qty: null, reason: "needs_density", warnings };
    if (u.ambiguousOz) warnings.push("Interpreted 'oz' as weight ounces, then converted with density.");
    return { ok: true, qty: (n * u.factor) / density, reason: null, warnings };
  }
  if (u.kind === "volume" && targetBase === "g") {
    if (!hasDensity) return { ok: false, qty: null, reason: "needs_density", warnings };
    return { ok: true, qty: n * u.factor * density, reason: null, warnings };
  }

  // count ↔ mass/volume requires an explicit per-each size.
  const eachSize = bridges.eachSize;
  const eachNorm = eachSize ? normalizeUnit(eachSize.unit) : null;
  const eachQty = eachSize ? Number(eachSize.qty) : NaN;
  const eachOk = eachNorm && Number.isFinite(eachQty) && eachQty > 0 &&
    (eachNorm.kind === "mass" || eachNorm.kind === "volume");

  if (u.kind === "count" && (targetBase === "g" || targetBase === "ml")) {
    if (!eachOk) return { ok: false, qty: null, reason: "needs_each_size", warnings };
    const eachInBase = convertToBase(eachQty, eachSize.unit, targetBase, { densityGPerMl: bridges.densityGPerMl });
    if (!eachInBase.ok) return { ok: false, qty: null, reason: eachInBase.reason, warnings };
    return { ok: true, qty: n * u.factor * eachInBase.qty, reason: null, warnings };
  }
  if ((u.kind === "mass" || u.kind === "volume") && targetBase === "each") {
    if (!eachOk) return { ok: false, qty: null, reason: "needs_each_size", warnings };
    const eachInKind = convertToBase(eachQty, eachSize.unit, u.base, { densityGPerMl: bridges.densityGPerMl });
    if (!eachInKind.ok || !eachInKind.qty) return { ok: false, qty: null, reason: eachInKind.reason || "unsupported", warnings };
    return { ok: true, qty: (n * u.factor) / eachInKind.qty, reason: null, warnings };
  }

  return { ok: false, qty: null, reason: "unsupported", warnings };
}

// ── Pack-size parsing ─────────────────────────────────────────────────────────

const NUM = "(\\d+(?:\\.\\d+)?)";

/**
 * Best-effort structured parse of an inventory pack_size string plus the
 * item's unit/case_quantity columns. Returns null fields rather than guessing.
 *
 * Recognized shapes (case-insensitive):
 *   "4-1GAL", "4/1 GAL", "4x1gal", "24-16 OZ"   → packCount × unitQty unit
 *   "50 LB", "50LB", "5 gal"                     → single pack quantity
 *   "6-#10", "6/#10"                             → count of #10 cans (size unresolved)
 *   "12 ct", "24 each"                           → count packs
 *
 * @returns {{ parsed: boolean, packCount: number|null, unitQty: number|null,
 *             unitRaw: string|null, unitNorm: object|null, special: string|null,
 *             confidence: number }}
 */
/**
 * Normalize a measure phrase from a pack description. Real supplier strings
 * append container/descriptor words to the unit ("1 lb bag", "8 oz pack",
 * "20 lb wheel avg") — try the whole phrase first, then progressively drop
 * trailing words until a measurable unit remains.
 */
function normalizeMeasurePhrase(phrase) {
  const direct = normalizeUnit(phrase);
  if (direct.kind !== "unknown") return { unitNorm: direct, matched: phrase, stripped: false, strippedContainerOnly: false, droppedTokens: [] };
  const tokens = String(phrase).trim().split(/\s+/);
  for (let end = tokens.length - 1; end >= 1; end -= 1) {
    const prefix = tokens.slice(0, end).join(" ");
    const norm = normalizeUnit(prefix);
    if (norm.kind === "mass" || norm.kind === "volume" || norm.kind === "count") {
      // "25 lb sack" drops only the container word — that carries no real
      // ambiguity, unlike dropping free-text descriptors ("wheel avg").
      const dropped = tokens.slice(end).map((t) => t.toLowerCase().replace(/[.,]+$/, ""));
      const strippedContainerOnly = dropped.every((t) => CONTAINER_WORDS.has(t));
      return { unitNorm: norm, matched: prefix, stripped: true, strippedContainerOnly, droppedTokens: dropped };
    }
  }
  return { unitNorm: direct, matched: phrase, stripped: false, strippedContainerOnly: false, droppedTokens: [] };
}

// Catch-weight qualifiers suppliers append to a size: "3.25 LB AVG",
// "12-16 oz avg wt", "approx 60 lb". They mark the size as NOMINAL — real, but
// per-piece weight varies — never as a different quantity.
const QUALIFIER_WORDS = new Set([
  "avg", "average", "averages", "approx", "approximately", "appx", "nom",
  "nominal", "abt", "about",
]);
// Words that ride along with a qualifier without adding meaning ("avg wt").
const QUALIFIER_TRAILERS = new Set(["wt", "weight", "each", "ea", "per"]);
const LEADING_QUALIFIER_RE = /^(?:avg\.?|average|approx\.?|approximately|appx\.?|abt\.?|about|~)\s+/i;

function phraseHasQualifier(droppedTokens) {
  return (droppedTokens || []).some((t) => QUALIFIER_WORDS.has(t));
}

/** Strip trailing qualifier words ("LB AVG", "OZ AVG WT") off a measure phrase. */
function splitQualifierTail(phrase) {
  const tokens = String(phrase).trim().split(/\s+/);
  let end = tokens.length;
  let approximate = false;
  while (end > 1) {
    const t = tokens[end - 1].toLowerCase().replace(/[.,]+$/, "");
    if (QUALIFIER_WORDS.has(t)) { approximate = true; end -= 1; continue; }
    if (approximate && QUALIFIER_TRAILERS.has(t)) { end -= 1; continue; }
    break;
  }
  return { phrase: tokens.slice(0, end).join(" "), approximate };
}

export function parsePackSize(packSize) {
  const out = {
    parsed: false, packCount: null, unitQty: null, unitRaw: null, unitNorm: null,
    special: null, confidence: 0,
    // Structure of the reading itself, not of the measurement:
    // packCountExplicit — the leading N was written by the vendor ("20/12 OZ"),
    //   as opposed to the implied 1 of a single measure ("50 LB").
    // unitQtyMin/Max + approximate — catch-weight ranges and AVG sizes
    //   ("12/12-16 OZ AVG"): unitQty holds the midpoint/nominal value.
    packCountExplicit: false, unitQtyMin: null, unitQtyMax: null,
    approximate: false, qualifier: null,
  };
  if (packSize == null) return out;
  let s = String(packSize).trim();
  if (!s) return out;

  // "24-count case" / "24 count" — a stated count of pieces with a hyphen the
  // number regex would otherwise trip on. Normalized to the ct form the count
  // table already reads.
  s = s.replace(/^(\d+)\s*-\s*(?:count|ct)\b/i, "$1 ct");

  // "#10 can" style institutional sizes: record the shape, don't guess grams.
  // Real supplier strings append container words ("6/#10 case") — tolerate
  // trailing can/container words so the shape is still recognized. The
  // suffix is a single character class (linear scan), never nested
  // quantifiers — pack strings come from supplier data, and a pathological
  // string must not be able to stall the edge function on backtracking.
  let m = s.match(new RegExp(`^${NUM}\\s*[-/xX×]\\s*#(\\d+)([a-zA-Z ]*)$`, "i"));
  if (m) {
    const trailing = m[3].trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (trailing.every((t) => t === "can" || t === "cans" || CONTAINER_WORDS.has(t))) {
      return { ...out, parsed: true, packCount: Number(m[1]), unitQty: 1, unitRaw: `#${m[2]} can`, special: "number_can", confidence: 0.5, packCountExplicit: true };
    }
  }

  // N/M1-M2<unit> — a per-piece catch-weight RANGE: "12/3-3.5 LB",
  // "12/12-16 OZ AVG", "6 x 60-70 oz". The first separator must be / or x
  // (a leading hyphen pair "4-1GAL" is the plain multi-pack form below).
  // unitQty carries the midpoint; min/max keep the stated bounds.
  m = s.match(new RegExp(`^${NUM}\\s*[/xX×]\\s*${NUM}\\s*[-–—]\\s*${NUM}\\s*([a-zA-Z#][a-zA-Z .#-]*)$`));
  if (m) {
    const tail = splitQualifierTail(m[4].trim());
    const { unitNorm, matched, stripped, strippedContainerOnly, droppedTokens } = normalizeMeasurePhrase(tail.phrase);
    const lo = Math.min(Number(m[2]), Number(m[3]));
    const hi = Math.max(Number(m[2]), Number(m[3]));
    const mid = (lo + hi) / 2;
    if (unitNorm.kind !== "unknown") {
      return {
        ...out, parsed: true, packCount: Number(m[1]), unitQty: mid, unitRaw: matched, unitNorm,
        confidence: unitNorm.kind === "container" ? 0.4 : stripped ? (strippedContainerOnly ? 0.9 : 0.85) : 0.9,
        packCountExplicit: true, unitQtyMin: lo, unitQtyMax: hi,
        approximate: true, qualifier: "range",
      };
    }
    return {
      ...out, parsed: true, packCount: Number(m[1]), unitQty: mid, unitRaw: tail.phrase, unitNorm, confidence: 0.3,
      packCountExplicit: true, unitQtyMin: lo, unitQtyMax: hi, approximate: true, qualifier: "range",
    };
  }

  // M1-M2<unit> with an explicit qualifier — "12-16 oz avg": ONE piece of a
  // stated weight range. Without the qualifier a hyphen pair keeps its long-
  // standing multi-pack meaning ("24-16 OZ" is 24 × 16 oz), so the qualifier
  // is what disambiguates.
  m = s.match(new RegExp(`^${NUM}\\s*[-–—]\\s*${NUM}\\s*([a-zA-Z#][a-zA-Z .#-]*)$`));
  if (m) {
    const tail = splitQualifierTail(m[3].trim());
    if (tail.approximate) {
      const { unitNorm, matched } = normalizeMeasurePhrase(tail.phrase);
      const lo = Math.min(Number(m[1]), Number(m[2]));
      const hi = Math.max(Number(m[1]), Number(m[2]));
      if (unitNorm.kind === "mass" || unitNorm.kind === "volume" || unitNorm.kind === "count") {
        return {
          ...out, parsed: true, packCount: 1, unitQty: (lo + hi) / 2, unitRaw: matched, unitNorm,
          confidence: 0.85, unitQtyMin: lo, unitQtyMax: hi, approximate: true, qualifier: "range",
        };
      }
    }
  }

  // N<sep> AVG M<unit> — "12 / AVG 3.25 LB", "4 x approx 60 lb".
  m = s.match(new RegExp(`^${NUM}\\s*[-/xX×]\\s*((?:avg\\.?|average|approx\\.?|approximately|appx\\.?|abt\\.?|about|~)\\s+)${NUM}\\s*([a-zA-Z#][a-zA-Z .#-]*)$`, "i"));
  if (m) {
    const tail = splitQualifierTail(m[4].trim());
    const { unitNorm, matched, stripped, strippedContainerOnly } = normalizeMeasurePhrase(tail.phrase);
    if (unitNorm.kind !== "unknown") {
      return {
        ...out, parsed: true, packCount: Number(m[1]), unitQty: Number(m[3]), unitRaw: matched, unitNorm,
        confidence: unitNorm.kind === "container" ? 0.4 : stripped ? (strippedContainerOnly ? 0.9 : 0.85) : 0.9,
        packCountExplicit: true, approximate: true, qualifier: "average",
      };
    }
  }

  // N-M<unit> / N/M<unit> / NxM<unit>  e.g. "4-1GAL", "24/16 oz", "6x5 lb"
  m = s.match(new RegExp(`^${NUM}\\s*[-/xX×]\\s*${NUM}\\s*([a-zA-Z#][a-zA-Z .#-]*)$`));
  if (m) {
    const phrase = m[3].trim();
    const { unitNorm, matched, stripped, strippedContainerOnly, droppedTokens } = normalizeMeasurePhrase(phrase);
    const approximate = phraseHasQualifier(droppedTokens);
    if (unitNorm.kind !== "unknown") {
      return {
        ...out, parsed: true, packCount: Number(m[1]), unitQty: Number(m[2]), unitRaw: matched, unitNorm,
        confidence: unitNorm.kind === "container" ? 0.4 : stripped ? (strippedContainerOnly ? 0.9 : 0.85) : 0.9,
        packCountExplicit: true, approximate, qualifier: approximate ? "average" : null,
      };
    }
    return { ...out, parsed: true, packCount: Number(m[1]), unitQty: Number(m[2]), unitRaw: phrase, unitNorm, confidence: 0.3, packCountExplicit: true };
  }

  // AVG M<unit> — the size side of a dual measure ("AVG 60 LB").
  m = s.match(LEADING_QUALIFIER_RE) ? s.replace(LEADING_QUALIFIER_RE, "").match(new RegExp(`^${NUM}\\s*([a-zA-Z#][a-zA-Z .#-]*)$`)) : null;
  if (m) {
    const tail = splitQualifierTail(m[2].trim());
    const { unitNorm, matched } = normalizeMeasurePhrase(tail.phrase);
    if (unitNorm.kind === "mass" || unitNorm.kind === "volume" || unitNorm.kind === "count") {
      return {
        ...out, parsed: true, packCount: 1, unitQty: Number(m[1]), unitRaw: matched, unitNorm,
        confidence: 0.8, approximate: true, qualifier: "average",
      };
    }
  }

  // Single quantity + unit: "50 LB", "5gal", "750 ml", "12 ct", "10 lb case"
  m = s.match(new RegExp(`^${NUM}\\s*([a-zA-Z#][a-zA-Z .#-]*)$`));
  if (m) {
    const phrase = m[2].trim();
    const { unitNorm, matched, stripped, strippedContainerOnly, droppedTokens } = normalizeMeasurePhrase(phrase);
    const approximate = phraseHasQualifier(droppedTokens);
    if (unitNorm.kind !== "unknown") {
      return {
        ...out, parsed: true, packCount: 1, unitQty: Number(m[1]), unitRaw: matched, unitNorm,
        confidence: unitNorm.kind === "container" ? 0.35 : stripped ? (strippedContainerOnly ? 0.85 : 0.8) : 0.85,
        approximate, qualifier: approximate ? "average" : null,
      };
    }
    return { ...out, parsed: true, packCount: 1, unitQty: Number(m[1]), unitRaw: phrase, unitNorm, confidence: 0.25 };
  }

  return out;
}

// ── Conversion record builder ─────────────────────────────────────────────────

function toFiniteOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function roundTo(n, dp) {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

function approximatelyEqual(a, b, relativeTolerance = 0.02) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const scale = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) <= relativeTolerance * scale;
}

function caseQuantityRepeatsPackTotal(pack, caseQty, itemUnitNorm) {
  if (!pack?.unitNorm || pack.packCount !== 1 || !(caseQty > 1)) return false;
  if (approximatelyEqual(caseQty, Number(pack.unitQty), 0.000001)) return true;
  if (!itemUnitNorm?.base || itemUnitNorm.base !== pack.unitNorm.base) return false;

  // Some catalog rows store the same case total twice in different units:
  // pack_size "40 lb case", unit "oz", case_quantity 640. In that shape,
  // case_quantity is not a count of 640 inner 40 lb packs.
  const catalogTotal = caseQty * itemUnitNorm.factor;
  const parsedTotal = Number(pack.unitQty) * pack.unitNorm.factor;
  return approximatelyEqual(catalogTotal, parsedTotal);
}

export function fmtQty(n) {
  if (!Number.isFinite(n)) return "?";
  const rounded = Math.abs(n) >= 100 ? roundTo(n, 1) : roundTo(n, 2);
  return rounded.toLocaleString("en-US");
}

/**
 * Validate a purchase-side pack-contents hint (packContentsHintFromOptions'
 * shape, or a rehydrated item_measurement_knowledge row) into the fields the
 * derivation consumes. Null when the hint is unusable: no positive quantity,
 * a unit this engine cannot normalize, or a family that contradicts the
 * unit's own family — corrupt knowledge must read as absent, never as fact.
 */
function normalizePackContentsHint(raw) {
  if (!raw || typeof raw !== "object") return null;
  const qty = toFiniteOrNull(raw.quantityPerPackage);
  const unit = typeof raw.unit === "string" ? raw.unit.trim() : "";
  if (qty == null || qty <= 0 || !unit) return null;
  const norm = normalizeUnit(unit);
  if (!norm.base || norm.kind === "unknown" || norm.kind === "container") return null;
  if (raw.family && raw.family !== norm.kind) return null;
  const positive = (v) => {
    const n = toFiniteOrNull(v);
    return n != null && n > 0 ? n : null;
  };
  return {
    quantityPerPackage: qty,
    unit,
    norm,
    quantityPerEach: positive(raw.quantityPerEach),
    packageCount: positive(raw.packageCount),
    source: typeof raw.source === "string" ? raw.source : "ai_estimate",
    confidence: toFiniteOrNull(raw.confidence),
  };
}

// A hint-derived conversion is capped below every deterministic parse: the
// profile's own provenance sets the ceiling, and an AI estimate can never
// read as more certain than a parsed pack.
function contentsHintConfidence(hint) {
  const cap = hint.source === "user" ? 0.9
    : hint.source === "explicit" ? 0.85
    : hint.source === "deterministic" ? 0.75
    : 0.6;
  const own = hint.confidence != null ? hint.confidence : 0.5;
  return roundTo(Math.min(cap, Math.max(0.2, own)), 2);
}

/**
 * Derive a conversion record for one inventory item from its own pack fields,
 * without any AI input. AI/user layers can then fill gaps (density, each size,
 * yields, unparseable pack text) — but those additions keep their own `source`
 * and are never marked verified here.
 *
 * @param item inventory_items row: {id, description, unit, pack_size,
 *             case_quantity, case_price, unit_price}
 * @param hints optional purchase-side measurement knowledge:
 *             { eachSize: { qty, unit, source?, confidence? },
 *               packContents: { family, unit, quantityPerPackage,
 *                 quantityPerEach?, packageCount?, source?, confidence? } }.
 *             `eachSize` is the per-piece bridge a purchase option's durable
 *             measurement profile already established ("one ear ≈ 0.5 lb").
 *             `packContents` is the WHOLE-PACKAGE measurement the purchasing
 *             side objectified ("one case ≈ 48 oz") — consulted ONLY when
 *             this derivation's own pack facts produce no basis at all, so a
 *             pack wording no parser reads ("6 x 2/4 OZ") still converts
 *             from the answer the store already holds. Both are gap-fillers,
 *             never overrides; provenance lands in warnings and the result
 *             is ai_inferred, never verified. Recipe-form yields stay
 *             culinary facts owned here.
 * @returns conversion draft (subset of menu_costing_conversions columns)
 */
export function deriveConversionFromItem(item, hints = null) {
  const unitNorm = normalizeUnit(item.unit);
  const pack = parsePackSize(item.pack_size);
  const contentsHint = hints && typeof hints === "object"
    ? normalizePackContentsHint(hints.packContents)
    : null;
  const casePrice = toFiniteOrNull(item.case_price);
  const unitPrice = toFiniteOrNull(item.unit_price);
  const caseQty = toFiniteOrNull(item.case_quantity);

  const warnings = [];
  let baseUnit = null;
  let conversionType = "unknown";
  let usablePerPurchase = null; // in base units, before yields
  let purchaseUnit = null;
  let purchasePrice = null;
  let countUnit = null;
  let source = "unknown";
  let confidence = 0;

  // Preferred basis: case price + parsed pack ("case of 6 × 5 lb").
  const packBase = pack.parsed && pack.unitNorm && pack.unitNorm.base ? pack.unitNorm : null;
  // A single-quantity pack description ("50 LB") with case_quantity > 1
  // usually means a case of N such packs — otherwise the case price would
  // silently be spread over one pack and inflate cost N-fold. EXCEPT when
  // case_quantity merely repeats the pack's own quantity ("25 lb bag" with
  // case_quantity 25): stores often record the total measure there, and
  // multiplying would deflate the cost 25-fold.
  const impliedPackCount = () => {
    if (
      pack.packCount === 1 &&
      caseQty &&
      caseQty > 1 &&
      !caseQuantityRepeatsPackTotal(pack, caseQty, unitNorm)
    ) {
      return caseQty;
    }
    return pack.packCount;
  };
  if (casePrice && casePrice > 0 && packBase) {
    baseUnit = packBase.base;
    conversionType = packBase.kind;
    const packCount = impliedPackCount();
    if (packCount !== pack.packCount) {
      warnings.push(`Assumed 1 case = ${caseQty} × ${item.pack_size} (from the item's case quantity) — verify the pack.`);
    }
    usablePerPurchase = packCount * pack.unitQty * packBase.factor;
    purchaseUnit = "case";
    purchasePrice = casePrice;
    countUnit = packCount > 1 ? "pack" : null;
    source = "pack_parsed";
    confidence = packCount !== pack.packCount ? Math.min(pack.confidence, 0.6) : pack.confidence;
    pack.packCount = packCount;
  } else if (unitPrice && unitPrice > 0 && unitNorm.base && !(isTinyMeasureNorm(unitNorm) && packBase)) {
    // Fall back to the per-unit price against the item's own unit column.
    // "$6.50 per lb" → 453.59 g per lb is arithmetic, not interpretation —
    // the confidence reflects that certainty. An ambiguous "oz" unit keeps a
    // review nudge (fluid vs weight ounces is a real interpretation). A tiny
    // measure unit (ml/g) with a parseable pack skips this branch entirely —
    // the price belongs to the pack, not the millilitre.
    baseUnit = unitNorm.base;
    conversionType = unitNorm.kind;
    usablePerPurchase = unitNorm.factor;
    purchaseUnit = unitNorm.key;
    purchasePrice = unitPrice;
    source = "pack_parsed";
    confidence = unitNorm.ambiguousOz ? 0.8 : 0.9;
    if (unitNorm.ambiguousOz) warnings.push("Item unit 'oz' assumed to be weight ounces.");
    if (isTinyMeasureNorm(unitNorm)) {
      // No pack to reattach the price to — keep the arithmetic but flag it
      // loudly; a per-ml/per-g supplier price is almost certainly not real.
      confidence = 0.3;
      warnings.push(`A price per single ${unitNorm.key} is implausible for supplier pricing — verify what $${unitPrice} actually buys.`);
    }
  } else if (unitPrice && unitPrice > 0 && packBase) {
    // Price stored per container ("$35.00/case", "$30.24/sack", "$34.00/tin")
    // while the pack description says what the container holds — price the
    // parsed contents. Keep the item's own container word as the purchase
    // unit so price refreshes stay on the per-container price. A tiny
    // measure unit (ml/g) is data noise, never a container word.
    const tinyUnit = isTinyMeasureNorm(unitNorm);
    baseUnit = packBase.base;
    conversionType = packBase.kind;
    const packCount = impliedPackCount();
    if (packCount !== pack.packCount) {
      warnings.push(`Assumed 1 ${item.unit || "case"} = ${caseQty} × ${item.pack_size} (from the item's case quantity) — verify the pack.`);
    }
    let containerWord = unitNorm.key ||
      (item.unit && String(item.unit).trim() ? String(item.unit).trim().toLowerCase() : null);
    if (tinyUnit) containerWord = "pack";
    usablePerPurchase = packCount * pack.unitQty * packBase.factor;
    purchaseUnit = containerWord || "case";
    purchasePrice = unitPrice;
    countUnit = packCount > 1 ? "pack" : null;
    source = "pack_parsed";
    confidence = packCount !== pack.packCount ? Math.min(pack.confidence, 0.6) : Math.min(pack.confidence, tinyUnit ? 0.8 : 0.85);
    pack.packCount = packCount;
    warnings.push(tinyUnit
      ? `Item unit "${unitNorm.key}" cannot be a real selling unit — $${unitPrice} treated as the price of one "${item.pack_size}". Verify the pack matches.`
      : `Price is per ${containerWord || "container"} — treated as the price of one "${item.pack_size}". Verify the pack matches.`);
  } else if (casePrice && casePrice > 0 && caseQty && caseQty > 0 && unitNorm.base) {
    // case_price with case_quantity of the item's unit.
    baseUnit = unitNorm.base;
    conversionType = unitNorm.kind;
    usablePerPurchase = caseQty * unitNorm.factor;
    purchaseUnit = "case";
    purchasePrice = casePrice;
    countUnit = unitNorm.kind === "count" ? unitNorm.key : null;
    source = "pack_parsed";
    confidence = 0.55;
  } else if (contentsHint) {
    // The item's own pack facts produced no basis, but the purchasing side
    // has already objectified this package — a durable measurement profile
    // (or reused measurement knowledge) says what one purchase package
    // contains. Adopt it as the physical basis, clearly labelled and never
    // verified: the exact pack wording the parsers refuse ("6 x 2/4 OZ") is
    // the case this branch exists for. The price basis mirrors
    // purchaseBasisFromItem — the profile measures ONE purchase package,
    // which is the container the item's own price names.
    baseUnit = contentsHint.norm.base;
    conversionType = contentsHint.norm.kind;
    usablePerPurchase = contentsHint.quantityPerPackage * contentsHint.norm.factor;
    const priceBasis = purchaseBasisFromItem(item);
    purchaseUnit = priceBasis ? priceBasis.purchase_unit : "case";
    purchasePrice = priceBasis ? priceBasis.purchase_price : null;
    countUnit = contentsHint.packageCount != null && contentsHint.packageCount > 1 ? "pack" : null;
    source = "ai_inferred";
    confidence = contentsHintConfidence(contentsHint);
    warnings.push(
      `Package contents (≈${fmtQty(contentsHint.quantityPerPackage)} ${contentsHint.unit} per ${purchaseUnit}) reused from the vendor package measurement` +
      `${contentsHint.source === "ai_estimate" ? " (AI estimate)" : contentsHint.source === "user" ? " (user-confirmed)" : ""} — verify if this item's package differs.`,
    );
    if (!priceBasis) warnings.push("No usable price on this item.");
  } else {
    if (pack.special === "number_can") warnings.push("Pack uses #-can sizing; the can's net contents are not stated.");
    if (!packBase && item.pack_size) warnings.push(`Pack description "${item.pack_size}" could not be parsed into a measurable quantity.`);
    if (!casePrice && !unitPrice) warnings.push("No usable price on this item.");
  }

  const conv = {
    inventory_item_id: item.id,
    purchase_unit: purchaseUnit,
    pack_description: item.pack_size || null,
    case_quantity: pack.parsed ? pack.packCount : caseQty,
    count_unit: countUnit,
    base_unit: baseUnit,
    conversion_type: conversionType,
    density_g_per_ml: null,
    each_size: null,
    edible_yield_pct: null,
    process_yield_pct: null,
    usable_qty_per_purchase: usablePerPurchase != null ? roundTo(usablePerPurchase, 3) : null,
    purchase_price: purchasePrice,
    cost_per_base: null,
    source,
    confidence,
    verification_state: source === "pack_parsed" ? "unverified"
      : source === "ai_inferred" ? "ai_inferred" : "unknown",
    warnings,
  };
  // Purchase-side per-each knowledge fills the count bridge ONLY when this
  // derivation supplies none of its own (no explicit each_size, no implied
  // per-pack size from the case math) — the exact gap that otherwise queues
  // an AI unit-research question. Marked in warnings, never verified. The
  // whole-package hint's per-each bridge serves the same way when no
  // dedicated each-size hint was passed.
  let hintEach = hints && typeof hints === "object" ? hints.eachSize : null;
  if (!hintEach && contentsHint && contentsHint.quantityPerEach != null
    && (contentsHint.norm.kind === "mass" || contentsHint.norm.kind === "volume")) {
    hintEach = {
      qty: contentsHint.quantityPerEach,
      unit: contentsHint.unit,
      source: contentsHint.source,
    };
  }
  const hintQty = hintEach ? toFiniteOrNull(hintEach.qty) : null;
  const hintUnit = hintEach && typeof hintEach.unit === "string" ? hintEach.unit.trim() : "";
  if (hintQty != null && hintQty > 0 && hintUnit && effectiveEachSize(conv) == null) {
    conv.each_size = { qty: hintQty, unit: hintUnit };
    warnings.push(
      `Per-each size (~${hintQty} ${hintUnit}) reused from the vendor package measurement profile` +
      `${hintEach.source === "ai_estimate" ? " (AI estimate)" : ""} — verify if this item's pieces differ.`,
    );
  }
  return finalizeConversion(conv);
}

/**
 * Recompute the derived fields (usable qty after yields, cost per base,
 * readable chain, plausibility warnings) after any edit. Returns a NEW object.
 */
export function finalizeConversion(convIn) {
  const conv = { ...convIn };
  const warnings = Array.isArray(conv.warnings) ? [...conv.warnings.filter((w) => typeof w === "string")] : [];
  const structural = warnings.filter((w) => !w.startsWith("⚖"));

  const usableRaw = toFiniteOrNull(conv.usable_qty_per_purchase);
  const edible = toFiniteOrNull(conv.edible_yield_pct);
  const process = toFiniteOrNull(conv.process_yield_pct);
  const price = toFiniteOrNull(conv.purchase_price);

  let effective = usableRaw;
  if (effective != null) {
    if (edible != null) {
      if (edible <= 0 || edible > 100) structural.push(`⚖ Edible yield ${edible}% is outside the plausible 1–100% range.`);
      else effective *= edible / 100;
    }
    if (process != null) {
      if (process <= 0 || process > 100) structural.push(`⚖ Process yield ${process}% is outside the plausible 1–100% range.`);
      else effective *= process / 100;
    }
  }

  conv.cost_per_base = (price && price > 0 && effective && effective > 0)
    ? roundTo(price / effective, 6)
    : null;

  conv.chain = buildChainSteps({ ...conv, effective_usable: effective });
  conv.effective_usable_qty = effective != null ? roundTo(effective, 3) : null;
  conv.warnings = structural;
  return conv;
}

const BASE_LABEL = { g: "g", ml: "ml", each: "each" };

/**
 * Imperial equivalent of a base quantity, for chef-facing chain displays —
 * kitchens think in lb/oz and gal/qt/cups, not grams and milliliters.
 */
export function imperialHint(qty, base) {
  const n = toFiniteOrNull(qty);
  if (n == null || n <= 0) return null;
  if (base === "g") {
    const lb = n / MASS_UNITS.lb;
    if (lb >= 1) return `≈ ${fmtQty(lb)} lb`;
    return `≈ ${fmtQty(n / MASS_UNITS.oz)} oz`;
  }
  if (base === "ml") {
    const gal = n / VOLUME_UNITS.gal;
    if (gal >= 1) return `≈ ${fmtQty(gal)} gal`;
    const qt = n / VOLUME_UNITS.qt;
    if (qt >= 1) return `≈ ${fmtQty(qt)} qt`;
    const cups = n / VOLUME_UNITS.cup;
    if (cups >= 1) return `≈ ${fmtQty(cups)} cups`;
    return `≈ ${fmtQty(n / VOLUME_UNITS.floz)} fl oz`;
  }
  return null;
}

/** Human-readable conversion chain, e.g. the "1 case = 6 bags = 30 lb …" display. */
export function buildChainSteps(conv) {
  const steps = [];
  const base = conv.base_unit;
  if (!base) return steps;

  const packDesc = conv.pack_description ? String(conv.pack_description) : null;
  const purchase = conv.purchase_unit || "purchase unit";
  const caseQty = toFiniteOrNull(conv.case_quantity);
  const usable = toFiniteOrNull(conv.usable_qty_per_purchase);
  const edible = toFiniteOrNull(conv.edible_yield_pct);
  const process = toFiniteOrNull(conv.process_yield_pct);
  const effective = toFiniteOrNull(conv.effective_usable);

  steps.push({ label: `1 ${purchase}`, detail: packDesc ? `pack: ${packDesc}` : null });
  if (caseQty && caseQty > 1 && conv.count_unit) {
    steps.push({ label: `= ${fmtQty(caseQty)} ${conv.count_unit}${caseQty === 1 ? "" : "s"}`, detail: null });
  }
  if (usable != null) {
    steps.push({ label: `= ${fmtQty(usable)} ${BASE_LABEL[base]}`, detail: imperialHint(usable, base) });
  }
  if (usable != null && edible != null && edible > 0 && edible <= 100) {
    steps.push({ label: `× ${edible}% edible yield`, detail: null });
  }
  if (usable != null && process != null && process > 0 && process <= 100) {
    steps.push({ label: `× ${process}% process yield`, detail: null });
  }
  if (effective != null && (edible != null || process != null)) {
    steps.push({ label: `= ${fmtQty(effective)} usable ${BASE_LABEL[base]}`, detail: imperialHint(effective, base) });
  }
  return steps;
}

/**
 * The per-each size bridge for count↔mass/volume conversion. An explicit
 * `each_size` always wins when present (returned untouched, even if malformed,
 * so the caller's validation sees exactly what was stored). When it is absent
 * but the conversion already carries its case math — a finite case_quantity
 * and usable_qty_per_purchase against a g/ml base — the implied per-each size
 * (usable_qty_per_purchase / case_quantity, in the base unit) is used: the
 * calculated conversion already fixed how much one pack weighs, so a count
 * line should price through it instead of going unresolved.
 */
export function effectiveEachSize(conv) {
  if (!conv) return null;
  if (conv.each_size != null) return conv.each_size;
  const caseQty = toFiniteOrNull(conv.case_quantity);
  const usable = toFiniteOrNull(conv.usable_qty_per_purchase);
  if (caseQty != null && caseQty > 0 && usable != null && usable > 0 &&
    (conv.base_unit === "g" || conv.base_unit === "ml")) {
    return { qty: usable / caseQty, unit: conv.base_unit };
  }
  return null;
}

/**
 * Validate a proposed conversion edit and list the confirmations the UI must
 * collect before saving. Pure — does not save anything.
 *
 * @param dependentLineUnits units of the recipe lines that price through this
 *        conversion, so bridge gaps (mass↔volume without density, count
 *        bridges without a per-each size, ambiguous fluid/weight oz) warn
 *        BEFORE the save silently unresolves those lines.
 * @returns {{ ok: boolean, requiresConfirmation: string[], errors: string[] }}
 */
export function validateConversionEdit(current, patch, dependentLineUnits = []) {
  const requires = [];
  const errors = [];
  const next = { ...current, ...patch };

  const edible = next.edible_yield_pct != null ? Number(next.edible_yield_pct) : null;
  const process = next.process_yield_pct != null ? Number(next.process_yield_pct) : null;
  for (const [label, v] of [["Edible yield", edible], ["Process yield", process]]) {
    if (v != null && (!Number.isFinite(v) || v <= 0)) errors.push(`${label} must be a positive percentage.`);
    else if (v != null && v > 100) requires.push(`${label} of ${v}% is greater than physically plausible (over 100%).`);
  }

  const density = next.density_g_per_ml != null ? Number(next.density_g_per_ml) : null;
  if (density != null && (!Number.isFinite(density) || density <= 0)) errors.push("Density must be a positive number (g per ml).");
  if (density != null && Number.isFinite(density) && (density < 0.1 || density > 5)) {
    requires.push(`Density ${density} g/ml is outside the plausible range for food products (0.1–5).`);
  }

  const usable = next.usable_qty_per_purchase != null ? Number(next.usable_qty_per_purchase) : null;
  if (usable != null && (!Number.isFinite(usable) || usable <= 0)) errors.push("Package quantity must be a positive number.");

  if (current && current.verification_state === "user_verified") {
    const materially = ["usable_qty_per_purchase", "base_unit", "density_g_per_ml", "each_size",
      "edible_yield_pct", "process_yield_pct", "purchase_price", "case_quantity"]
      .some((k) => k in patch && JSON.stringify(patch[k]) !== JSON.stringify(current[k]));
    if (materially) requires.push("This conversion was previously verified — saving will replace a verified record.");
  }

  // Bridge checks against the lines that actually price through this record.
  if (errors.length === 0) {
    const merged = finalizeConversion(next);
    if (merged.base_unit && dependentLineUnits.length) {
      let needDensity = 0;
      let needEach = 0;
      let ambiguousOz = 0;
      for (const u of dependentLineUnits) {
        const norm = normalizeUnit(u);
        if (norm.kind === "unknown" || norm.kind === "container") continue;
        const probe = convertToBase(1, u, merged.base_unit, {
          densityGPerMl: merged.density_g_per_ml,
          eachSize: effectiveEachSize(merged),
        });
        if (!probe.ok && probe.reason === "needs_density") needDensity += 1;
        if (!probe.ok && probe.reason === "needs_each_size") needEach += 1;
        if (norm.ambiguousOz && merged.base_unit === "ml") ambiguousOz += 1;
      }
      if (needDensity) requires.push(`${needDensity} recipe line(s) convert weight↔volume against this item and will be UNRESOLVED without a density.`);
      if (needEach) requires.push(`${needEach} recipe line(s) need a per-each size bridge and will be UNRESOLVED without one.`);
      if (ambiguousOz) requires.push(`${ambiguousOz} recipe line(s) use ambiguous "oz" against a volumetric item — check fluid vs weight ounces.`);
    }
    if (merged.base_unit && merged.usable_qty_per_purchase == null) {
      requires.push("No usable package quantity is set — dependent lines cannot be costed until one is.");
    }
  }

  return { ok: errors.length === 0, requiresConfirmation: requires, errors };
}

// ── Purchase-unit → recipe-unit translation (subjective → objective) ──────────
//
// A supplier sells "a case, 24 ct" or "a bunch" — subjective purchase units.
// A recipe measures "2 oz" or "0.5 each" — objective units this engine can
// reduce to g/ml/each. The helpers below are the pure half of the translation
// step that makes the two agree: they describe the purchase unit in plain
// English for the AI, detect exactly which bridge values are missing, build
// OBJECTIVE research questions ("how many oz does one piece weigh?" — never
// "how much celery goes with wings?"), and merge the AI's answers back onto a
// conversion without ever touching user-verified records.

/** The objective units recipes are allowed to measure with. Both the recipe
 * parser prompts and the purchase-unit translator answer from this list, so
 * every recipe line can reach a conversion base (g/ml/each) via at most one
 * bridge (density or per-each size). */
export const RECIPE_UNIT_CHOICES = ["oz", "lb", "floz", "cup", "tbsp", "tsp", "each"];

/**
 * Plain-English description of how an inventory item is PURCHASED — the
 * subjective supplier wording ("sold by the case, pack \"24 ct\"") presented
 * to the AI translator the way a buyer would say it.
 */
export function describePurchaseUnit(item) {
  if (!item) return "unknown purchase unit";
  const rawUnitWord = item.unit == null ? "" : String(item.unit).trim();
  // A tiny measure word (ml/g) in the unit column is data noise, never the
  // container the item is actually bought by.
  const tinyUnitWord = isTinyMeasureNorm(normalizeUnit(rawUnitWord));
  const unitWord = tinyUnitWord ? "" : rawUnitWord;
  const packText = item.pack_size == null ? "" : String(item.pack_size).trim();
  const caseQty = toFiniteOrNull(item.case_quantity);
  const casePrice = toFiniteOrNull(item.case_price);
  const unitPrice = toFiniteOrNull(item.unit_price);

  const pack = parsePackSize(packText);
  let packPhrase = null;
  if (pack.parsed && pack.unitQty != null) {
    packPhrase = pack.packCount && pack.packCount > 1
      ? `${fmtQty(pack.packCount)} × ${fmtQty(pack.unitQty)} ${pack.unitRaw}`
      : `${fmtQty(pack.unitQty)} ${pack.unitRaw}`;
  }

  const container = unitWord || (packText ? (tinyUnitWord ? "pack" : "case") : "unit");
  const parts = [`sold by the ${container}`];
  if (packText) {
    const detail = packPhrase && packPhrase.toLowerCase() !== packText.toLowerCase()
      ? ` (${packPhrase} per ${container})` : "";
    parts.push(`pack "${packText}"${detail}`);
  } else if (caseQty && caseQty > 1) {
    parts.push(`${fmtQty(caseQty)} per ${container}`);
  }
  if (casePrice && casePrice > 0) parts.push(`$${casePrice.toFixed(2)} per case`);
  else if (unitPrice && unitPrice > 0) parts.push(`$${unitPrice.toFixed(2)} per ${container}`);
  return parts.join(", ");
}

/**
 * Price-independent probe: which bridge values does this conversion still
 * need before the given recipe-line units can convert into its base?
 *
 * A count↔mass/volume mismatch requires an EXPLICIT each_size here — the
 * implied case-math fallback (usable ÷ case_quantity) can silently mean "one
 * inner pack" (one 10 lb bag of a 4/10 lb wing case), which is never what a
 * per-piece recipe line ("4 pieces") intends. This mirrors the analyze run's
 * queueing rule so run and out-of-run paths translate the same chains.
 *
 * @param conv      menu_costing_conversions row (or draft) — may be null
 * @param lineUnits raw unit strings of the recipe lines pricing through it
 * @returns {{ blocked, needsBase, needsEachSize, needsDensity, unknownUnits: string[] }}
 */
export function conversionUnitGaps(conv, lineUnits = []) {
  const gaps = { blocked: false, needsBase: false, needsEachSize: false, needsDensity: false, unknownUnits: [] };
  if (!conv || !conv.base_unit || toFiniteOrNull(conv.usable_qty_per_purchase) == null) {
    gaps.blocked = true;
    gaps.needsBase = true;
  }
  const kinds = new Set();
  const seen = new Set();
  for (const raw of lineUnits) {
    const key = raw == null ? "" : String(raw).trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const norm = normalizeUnit(raw);
    if (norm.kind === "mass" || norm.kind === "volume" || norm.kind === "count") {
      kinds.add(norm.kind);
    } else {
      gaps.blocked = true;
      gaps.unknownUnits.push(String(raw));
    }
  }
  const baseKind = !conv ? null
    : conv.base_unit === "g" ? "mass"
    : conv.base_unit === "ml" ? "volume"
    : conv.base_unit === "each" ? "count" : null;
  if (baseKind && kinds.size) {
    const crossDensity = (kinds.has("mass") && baseKind === "volume") || (kinds.has("volume") && baseKind === "mass");
    const crossEach = (kinds.has("count") && baseKind !== "count") ||
      (baseKind === "count" && (kinds.has("mass") || kinds.has("volume")));
    if (crossDensity && toFiniteOrNull(conv.density_g_per_ml) == null) { gaps.blocked = true; gaps.needsDensity = true; }
    if (crossEach && conv.each_size == null) { gaps.blocked = true; gaps.needsEachSize = true; }
  }
  return gaps;
}

/**
 * AI yield answers arrive as either a percent (85) or a 0–1 fraction (0.85).
 * The engine's contract is PERCENT 1–100; a fraction applied verbatim reads
 * as "0.85% edible" and inflates cost 85-fold (a real production failure:
 * $9.18 for 1.5 oz of canned artichokes). Normalize to percent; reject
 * anything implausible. 1 is treated as the fraction 100% — no food is 1%
 * edible.
 */
export function normalizeYieldPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const pct = n <= 1 ? n * 100 : n;
  if (pct < 1 || pct > 100) return null;
  return roundTo(pct, 1);
}

/**
 * The price basis matching "the contents of ONE purchase unit". The purchase
 * unit the AI answers for is the container named by describePurchaseUnit —
 * the item's own unit word ("sold by the bunch") when present, else the case
 * — so the price MUST be the price of that same container:
 *   unit word "case" (or none) → case price, else the per-unit price;
 *   any other unit word ("bunch", "tin", "lb") → ONLY the per-unit price. A
 *   case price alone prices a DIFFERENT container (a case of 60 bunches is
 *   not one bunch) — attaching it to one unit's contents inflates cost by
 *   the case count, so no basis is returned instead.
 * Zero prices are absent, not free.
 */
export function purchaseBasisFromItem(item) {
  if (!item) return null;
  const casePrice = toFiniteOrNull(item.case_price);
  const unitPrice = toFiniteOrNull(item.unit_price);
  const casePos = casePrice && casePrice > 0 ? casePrice : null;
  const unitPos = unitPrice && unitPrice > 0 ? unitPrice : null;
  if (casePos == null && unitPos == null) return null;
  let unitWord = item.unit && String(item.unit).trim() ? String(item.unit).trim().toLowerCase() : "";
  // ml/g "units" are data noise — the per-unit price belongs to the pack.
  const tinyUnit = isTinyMeasureNorm(normalizeUnit(unitWord));
  if (tinyUnit) unitWord = "";
  if (!unitWord || unitWord === "case" || unitWord === "cs") {
    return casePos != null
      ? { purchase_price: casePos, purchase_unit: "case" }
      : { purchase_price: unitPos, purchase_unit: tinyUnit ? "pack" : (unitWord || "case") };
  }
  return unitPos != null ? { purchase_price: unitPos, purchase_unit: unitWord } : null;
}

/* ── Canonical inventory cost → recipe base cost ──────────────────────────── */

/**
 * Turn the canonical inventory cost (money per `inventory_items.unit`) into a
 * cost per the conversion's objective base unit (g / ml / each), using the
 * conversion's OWN physical facts as the bridge. Nothing physical is invented
 * and nothing physical is written back: this is the money half of the chain,
 * kept strictly separate from the package/yield facts a user may have
 * verified.
 *
 *   $3.20/lb  →  base g   →  $3.20 ÷ 453.59237 per g
 *   $12.00/gal → base ml  →  $12.00 ÷ 3785.411784 per ml
 *   $0.40 each → base each →  $0.40 per each
 *
 * Two ways the item's own unit can reach the base:
 *
 *   'unit_convert'  — the unit is a real measure (lb, gal, each): convert 1 of
 *                     it into the base and divide. Cross-family hops use the
 *                     conversion's stored density / each_size and NOTHING else.
 *   'purchase_unit' — the unit is a container word ("case", "bag") that names
 *                     the very purchase unit this conversion already sized, so
 *                     the canonical cost IS the price of one purchase unit and
 *                     `usable_qty_per_purchase` is its contents.
 *
 * Anything else is UNRESOLVED — reported as `ok: false` with a reason, never
 * as a zero cost.
 *
 * @param {object} args.canonicalUnitCost money per one `inventoryUnit`
 * @param {string} args.inventoryUnit     inventory_items.unit
 * @param {object} args.conversion        menu_costing_conversions row
 * @returns {{ ok: boolean,
 *             costPerRawBase: number|null,     $ per base unit BEFORE yields
 *             baseQtyPerInventoryUnit: number|null,
 *             impliedPurchasePrice: number|null, price of one purchase unit
 *             basis: 'unit_convert'|'purchase_unit'|null,
 *             baseUnit: string|null,
 *             reason: string|null, warnings: string[] }}
 */
export function costPerBaseFromCanonicalCost({ canonicalUnitCost, inventoryUnit, conversion } = {}) {
  const out = {
    ok: false,
    costPerRawBase: null,
    baseQtyPerInventoryUnit: null,
    impliedPurchasePrice: null,
    basis: null,
    baseUnit: conversion?.base_unit ?? null,
    reason: null,
    warnings: [],
  };
  const cost = toFiniteOrNull(canonicalUnitCost);
  if (cost == null || cost <= 0) return { ...out, reason: "no_canonical_cost" };
  if (!conversion || !conversion.base_unit) return { ...out, reason: "no_conversion" };

  const base = conversion.base_unit;
  const usable = toFiniteOrNull(conversion.usable_qty_per_purchase);

  // 1. The item's own unit is a real measure — pure arithmetic through the
  //    conversion's stored bridges.
  const converted = convertToBase(1, inventoryUnit, base, {
    densityGPerMl: conversion.density_g_per_ml,
    eachSize: effectiveEachSize(conversion),
  });
  if (converted.ok && converted.qty > 0) {
    const costPerRawBase = cost / converted.qty;
    return {
      ...out,
      ok: true,
      costPerRawBase,
      baseQtyPerInventoryUnit: converted.qty,
      impliedPurchasePrice: usable != null && usable > 0 ? costPerRawBase * usable : null,
      basis: "unit_convert",
      baseUnit: base,
      warnings: converted.warnings,
    };
  }

  // 2. The item's own unit is the container this conversion already sized
  //    ("sold by the case", purchase_unit "case"): the canonical cost is the
  //    price of one purchase unit, and its contents are already known.
  const unitWord = inventoryUnit == null ? "" : String(inventoryUnit).trim().toLowerCase();
  const purchaseWord = conversion.purchase_unit == null ? "" : String(conversion.purchase_unit).trim().toLowerCase();
  const unitNorm = normalizeUnit(unitWord);
  const containerish = unitNorm.kind === "container" || unitNorm.kind === "unknown";
  if (containerish && unitWord && unitWord === purchaseWord && usable != null && usable > 0) {
    return {
      ...out,
      ok: true,
      costPerRawBase: cost / usable,
      baseQtyPerInventoryUnit: usable,
      impliedPurchasePrice: cost,
      basis: "purchase_unit",
      baseUnit: base,
      warnings: [
        `Canonical cost is stated per "${unitWord}" — treated as the price of one ${purchaseWord} of "${conversion.pack_description || "this pack"}".`,
      ],
    };
  }

  // Legacy inventory can name the FULL package as its pricing unit. Accept
  // only the very pack this conversion sized, and prove that its explicit
  // contents agree. The shared reader owns interpretation; a similar wrapper,
  // unreadable pack, or a conflicting saved quantity is not enough evidence.
  const packWord = String(conversion.pack_description || "").trim().toLowerCase();
  if (unitWord && unitWord === packWord && usable != null && usable > 0) {
    const pack = readPack(inventoryUnit);
    const measured = pack.read && !pack.approximate && pack.confidence >= 0.9
      ? convertToBase(pack.totalQty, pack.unitToken, base, {
        densityGPerMl: conversion.density_g_per_ml, eachSize: effectiveEachSize(conversion),
      }) : null;
    if (measured?.ok && Math.abs(measured.qty - usable) <= Math.max(0.001, usable * 1e-6)) {
      return { ...out, ok: true, costPerRawBase: cost / usable,
        baseQtyPerInventoryUnit: usable, impliedPurchasePrice: cost,
        basis: "purchase_unit", baseUnit: base,
        warnings: [`Canonical cost is stated per the measured package "${unitWord}".`] };
    }
  }

  // Nothing bridges the item's unit to the recipe base. Say which bridge is
  // missing so the same research question the rest of the engine asks can be
  // raised, and leave the cost unresolved rather than zero.
  return {
    ...out,
    reason: converted.reason === "unknown_unit" ? "unresolved_inventory_unit" : converted.reason || "unsupported",
    warnings: converted.warnings,
  };
}

/**
 * Merge one AI unit-translation answer onto an existing conversion. Mirrors
 * the analyze-run assist contract exactly: a field the record already carries
 * is never overwritten (bridge values are additive), and user-edited or
 * user-verified records are never touched.
 *
 * @param existing menu_costing_conversions row
 * @param answer   one CONVERSION_ASSIST answer {base_unit, usable_qty_per_purchase,
 *                 count_unit, case_quantity, density_g_per_ml, each_size_qty,
 *                 each_size_unit, edible_yield_pct, recipe_unit?}
 * @param item     the inventory item (for purchase-price backfill) — optional
 * @returns {{ patch, merged, recipeUnit: string|null } | null}  null = nothing to apply.
 *          `patch` may be empty when only recipeUnit was usable — callers then
 *          record the recipe unit as evidence without altering the physics.
 */
export function applyConversionAssistAnswer(existing, answer, item = null) {
  if (!existing || existing.verification_state === "user_verified" || existing.source === "user") return null;
  if (!answer) return null;
  const patch = {};
  if (!existing.base_unit && (answer.base_unit === "g" || answer.base_unit === "ml" || answer.base_unit === "each")) {
    patch.base_unit = answer.base_unit;
    patch.conversion_type = answer.base_unit === "g" ? "mass" : answer.base_unit === "ml" ? "volume" : "count";
  }
  if (existing.usable_qty_per_purchase == null && answer.usable_qty_per_purchase != null &&
      Number.isFinite(Number(answer.usable_qty_per_purchase)) && Number(answer.usable_qty_per_purchase) > 0) {
    patch.usable_qty_per_purchase = Number(answer.usable_qty_per_purchase);
    // The answer defines the contents of ONE purchase unit — align the price
    // to that same container (the item's own unit word, per
    // purchaseBasisFromItem), replacing a stale wrong-basis price such as a
    // per-can $8.67 stored against whole-case contents.
    const basis = purchaseBasisFromItem(item);
    if (basis) {
      patch.purchase_price = basis.purchase_price;
      patch.purchase_unit = basis.purchase_unit;
    }
  }
  if (answer.density_g_per_ml != null && Number.isFinite(Number(answer.density_g_per_ml)) && Number(answer.density_g_per_ml) > 0) {
    patch.density_g_per_ml = Number(answer.density_g_per_ml);
  }
  if (answer.each_size_qty != null && answer.each_size_unit &&
      Number.isFinite(Number(answer.each_size_qty)) && Number(answer.each_size_qty) > 0) {
    patch.each_size = { qty: Number(answer.each_size_qty), unit: String(answer.each_size_unit) };
  }
  if (answer.edible_yield_pct != null && existing.edible_yield_pct == null) {
    const yieldPct = normalizeYieldPct(answer.edible_yield_pct);
    if (yieldPct != null) patch.edible_yield_pct = yieldPct;
  }
  if (answer.case_quantity != null && existing.case_quantity == null) patch.case_quantity = Number(answer.case_quantity);
  if (answer.count_unit && !existing.count_unit) patch.count_unit = String(answer.count_unit);

  // The translated recipe unit is advice for the recipe side (default line
  // unit, editor hint); accept it only when it is genuinely objective.
  const recipeNorm = answer.recipe_unit ? normalizeUnit(answer.recipe_unit) : null;
  const recipeUnit = recipeNorm && (recipeNorm.kind === "mass" || recipeNorm.kind === "volume" || recipeNorm.kind === "count")
    ? recipeNorm.key
    : null;

  if (!Object.keys(patch).length && !recipeUnit) return null;
  const merged = finalizeConversion({ ...existing, ...patch });
  return { patch, merged, recipeUnit };
}

/**
 * The OBJECTIVE research question that unblocks one line↔conversion pair.
 * Never the subjective recipe question ("how much celery is served with
 * wings?") — always the measurable purchase-side one ("how much does one of
 * the 24 pieces in the case weigh, in oz?") so a findable real-world value
 * completes the chain and the recipe quantity stays the chef's number.
 *
 * @param reason priceRecipeQty failure reason (needs_each_size |
 *               needs_density | unknown_unit)
 * @returns {{ kind, unitWord: string|null, text } | null}
 */
export function buildUnitResearchQuestion(line, item, conversion, reason) {
  if (!item || !conversion) return null;
  const packText = item.pack_size ? String(item.pack_size).trim() : "";
  const product = `${item.description}${packText ? ` (pack: ${packText})` : ""}`;
  if (reason === "needs_each_size") {
    // Which "piece" needs a size depends on the direction of the bridge:
    // a counted pack (base each) needs the size of one pack piece; a
    // weight/volume pack with a counted recipe line needs the size of one
    // piece as the recipe counts them.
    const lineNorm = normalizeUnit(line ? line.unit : null);
    let pieceWord = "one whole piece/unit";
    if (conversion.base_unit === "each") {
      const perPurchase = toFiniteOrNull(conversion.usable_qty_per_purchase);
      const caseQty = toFiniteOrNull(conversion.case_quantity);
      const count = perPurchase && perPurchase > 1 ? perPurchase : (caseQty && caseQty > 1 ? caseQty : null);
      pieceWord = count
        ? `one of the ${fmtQty(count)} countable pieces in one ${conversion.purchase_unit || "purchase unit"}`
        : "one whole countable piece";
    } else if (lineNorm.kind === "count" && lineNorm.key && lineNorm.key !== "each") {
      pieceWord = `one ${lineNorm.key.replace(/s$/, "")}`;
    }
    const text = conversion.base_unit === "ml"
      ? `On average, how many fl oz is ${pieceWord} of "${product}"?`
      : `On average, how much does ${pieceWord} of "${product}" weigh, in oz?`;
    return { kind: "each_size", unitWord: null, text };
  }
  if (reason === "needs_density") {
    return {
      kind: "density",
      unitWord: null,
      text: `What is the typical density of "${item.description}" in grams per milliliter?`,
    };
  }
  if (reason === "unknown_unit") {
    const unitWord = line && line.unit != null ? String(line.unit).trim() : "";
    if (!unitWord) return null;
    return {
      kind: "unit_equivalent",
      unitWord,
      text: `Express one "${unitWord}" of "${product}" as a quantity of oz, floz, or whole "each" units (e.g. 1 slice of an artisan loaf ≈ 1.5 oz; 1 bunch of fresh dill ≈ 1 oz; 1 twist ≈ 0.1 each of a lemon).`,
    };
  }
  if (reason === "missing_pack_contents") {
    // The pack's total contents are the unknown (a #10-can case, a bunch, an
    // unparseable pack) — cost_per_base stays null however well the line's
    // unit converts. Ask for the objective total, never the recipe amount.
    return {
      kind: "pack_contents",
      unitWord: null,
      text: `In TOTAL, how much usable product is in ONE purchase unit of "${item.description}" (${describePurchaseUnit(item)})? Answer with the total quantity in oz (weight), floz (volume), or each (count) for the WHOLE purchase unit (e.g. a 6/#10 case of roasted red peppers ≈ 6 cans × 100 oz ≈ 600 oz; one bunch of fresh dill ≈ 1 oz).`,
    };
  }
  return null;
}

/**
 * Convert a recipe line quantity into the conversion's base unit and price it.
 *
 * @returns {{ ok, reason, qtyBase, cost, warnings[] }}
 *   reason ∈ needs_density | needs_each_size | unknown_unit | no_conversion |
 *            no_price | bad_qty | unsupported
 */
export function priceRecipeQty(qty, unitRaw, conv) {
  if (!conv || !conv.base_unit) return { ok: false, reason: "no_conversion", qtyBase: null, cost: null, warnings: [] };
  const res = convertToBase(qty, unitRaw, conv.base_unit, {
    densityGPerMl: conv.density_g_per_ml,
    eachSize: effectiveEachSize(conv),
  });
  if (!res.ok) return { ok: false, reason: res.reason, qtyBase: null, cost: null, warnings: res.warnings };
  const warnings = [...res.warnings];
  const u = normalizeUnit(unitRaw);
  if (u.ambiguousOz && conv.base_unit === "ml") {
    warnings.push("Recipe uses 'oz' against a volumetric item — check fluid vs weight ounces.");
  }
  const costPerBase = toFiniteOrNull(conv.cost_per_base);
  if (costPerBase == null) return { ok: false, reason: "no_price", qtyBase: res.qty, cost: null, warnings };
  return { ok: true, reason: null, qtyBase: res.qty, cost: roundTo(res.qty * costPerBase, 4), warnings };
}
